import { useState, useEffect } from 'react';

export const useEventHandlers = ({
  onExpand,
  onCollapse,
  setEventsExpanded,
  updateRSVP,
  updateEvent,
  organizations,
  containerRef,
  eventsExpanded,
  rsvpState,
  events,
}) => {
  const [editingEvent, setEditingEvent] = useState(null);
  const [tempContent, setTempContent] = useState({});
  const [isRecurring, setIsRecurring] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [showDetails, setShowDetails] = useState(null);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [justSaved, setJustSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localEvents, setLocalEvents] = useState(events || []);

  // Keep localEvents in sync with events prop
  useEffect(() => {
    setLocalEvents(events || []);
  }, [events]);

  useEffect(() => {
    // console.log('TempContent updated:', tempContent);
  }, [tempContent]);

  const updateContent = (path, value) => {
    setJustSaved(false);
    setIsSaving(false);
    setTempContent(prev => {
      // Handle nested paths like 'meta.guests[0].name' or 'meta.resources'
      const parts = path.split('.');
      const result = { ...prev };
      let current = result;
      
      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        const isArray = part.includes('[');
        if (isArray) {
          // Handle array paths like 'meta.guests[0]'
          const [arrayName, indexStr] = part.split('[');
          const index = parseInt(indexStr);
          current[arrayName] = [...(current[arrayName] || [])];
          current = current[arrayName];
          if (index >= current.length) {
            // Auto-expand array if needed
            current.push({});
          }
          current = current[index];
        } else {
          current[part] = current[part] || {};
          current = current[part];
        }
      }
      
      const lastPart = parts[parts.length - 1];
      current[lastPart] = value;
      return result;
    });
  };

  const handleSave = async (event) => {
    try {
      setIsSaving(true);
      
      // For new events, don't include the id field at all
      const eventData = {
        organization_id: event.organization_id,
        title: tempContent.title,
        description: tempContent.description,
        body_content: tempContent.body_content,
        event_date: tempContent.event_date,
        event_time: tempContent.event_time,
        image_url: tempContent.image_url,
        hosts: tempContent.hosts,
        is_recurring: tempContent.is_recurring,
        recurrence_pattern: tempContent.recurrence_pattern,
        meta: tempContent.meta
      };

      // Only include id if it's an existing event
      if (event.id !== 'new') {
        eventData.id = event.id;
      }

      const { data, error } = await updateEvent(eventData);

      if (error) throw error;

      // Update the editing event with the returned data
      setEditingEvent(data);
      setTempContent({
        title: data.title,
        description: data.description,
        body_content: data.body_content || '',
        image_url: data.image_url,
        event_date: data.event_date,
        event_time: data.event_time,
        hosts: data.hosts || [],
        is_recurring: data.is_recurring || false,
        recurrence_pattern: data.recurrence_pattern || null,
        meta: {
          zoom_link: data.meta?.zoom_link || '',
          resources: data.meta?.resources || [],
          guests: data.meta?.guests || []
        }
      });

      // Show "Saving..." for 2 seconds before showing "Saved!"
      setTimeout(() => {
        setIsSaving(false);
        setJustSaved(true);
      }, 2000);
    } catch (error) {
      setIsSaving(false);
      console.error('Failed to update event:', error);
      alert('Failed to update event. Please try again.');
    }
  };

  const handleDelete = async (event) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        const { error } = await updateEvent({
          id: event.id,
          organization_id: event.organization_id,
          _delete: true
        });

        if (error) throw error;

        setEditingEvent(null);
        setTempContent({});
      } catch (error) {
        console.error('Failed to delete event:', error);
        alert('Failed to delete event. Please try again.');
      }
    }
  };

  const handleRSVP = async (eventId) => {
    try {
      const newStatus = !rsvpState[eventId];
      const { error } = await updateRSVP(eventId, newStatus);

      if (error) {
        console.error('Failed to update RSVP:', error);
        alert('Unable to update RSVP. Please try again.');
      }
    } catch (error) {
      console.error('Failed to update RSVP:', error);
      alert('Unable to update RSVP. Please try again.');
    }
  };

  const handleExpandClick = (id) => {
    if (!expandedId) {
      setSavedScrollPosition(containerRef.current?.scrollTop || 0);
      onExpand();
    } else {
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = savedScrollPosition;
        }
      }, 0);
    }
    setExpandedId(expandedId === id ? null : id);
    if (expandedId === id) {
      onCollapse();
    }
  };

  const handleEventClick = (id) => {
    if (editingEvent) return;
    
    const willShowDetails = showDetails !== id;
    setShowDetails(willShowDetails ? id : null);
    
    if (!eventsExpanded && willShowDetails) {
      setEventsExpanded(true);
    }
  };

  const handleEditClick = (e, event) => {
    e.stopPropagation();
    setJustSaved(false);
    setIsSaving(false);
    console.log('Event being edited:', event);
    console.log('Event meta data:', event.meta);
    setEditingEvent(event);
    setTempContent({
      title: event.title,
      description: event.description,
      body_content: event.body_content || '',
      image_url: event.image_url,
      event_date: event.event_date,
      event_time: event.event_time,
      hosts: event.hosts || [],
      is_recurring: event.is_recurring || false,
      recurrence_pattern: event.recurrence_pattern || null,
      meta: {
        zoom_link: event.meta?.zoom_link || '',
        resources: event.meta?.resources || [],
        guests: event.meta?.guests || []
      }
    });
    console.log('TempContent after setting:', tempContent);
    setIsRecurring(event.is_recurring || false);
  };

  const handleAddEventClick = (e) => {
    e.stopPropagation();
    setJustSaved(false);
    setIsSaving(false);
    
    const newEvent = {
      id: 'new',
      title: '',
      description: '',
      body_content: '',
      image_url: '',
      event_date: new Date().toISOString().split('T')[0],
      event_time: new Date().toTimeString().slice(0, 5),
      organization_id: organizations?.admin?.[0]?.organizationId,
      hosts: [],
      is_recurring: false,
      recurrence_pattern: null,
      meta: {
        zoom_link: '',
        resources: [],
        guests: []
      }
    };

    // Add the new event to local events
    setLocalEvents(currentEvents => [newEvent, ...currentEvents]);

    setEditingEvent(newEvent);
    setTempContent({
      title: 'New Event',
      description: '',
      body_content: '',
      image_url: '',
      event_date: new Date().toISOString().split('T')[0],
      event_time: new Date().toTimeString().slice(0, 5),
      hosts: [],
      is_recurring: false,
      recurrence_pattern: null,
      meta: {
        zoom_link: '',
        resources: [],
        guests: []
      }
    });
    setIsRecurring(false);
    onExpand();
  };

  const handleCancel = () => {
    // Remove the temporary event from local events
    if (editingEvent?.id === 'new') {
      setLocalEvents(currentEvents => currentEvents.filter(e => e.id !== 'new'));
    }
    setEditingEvent(null);
    setTempContent({});
    setIsRecurring(false);
    setActiveTab('content');
    setJustSaved(false);
    setIsSaving(false);
  };

  return {
    // States
    editingEvent,
    tempContent,
    isRecurring,
    activeTab,
    showDetails,
    expandedId,
    justSaved,
    isSaving,
    localEvents,
    // Setters
    setActiveTab,
    setEditingEvent,
    setExpandedId,
    // Handlers
    updateContent,
    handleSave,
    handleDelete,
    handleRSVP,
    handleExpandClick,
    handleEventClick,
    handleEditClick,
    handleAddEventClick,
    handleCancel,
  };
}; 